import React, { useContext } from 'react'
import { Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import UserIcon from '@BUP/assets/Icons/user_icon.svg'
import PageLayout from '@BUP/components/Layout/Page'
import TitleDashboard from '@BUP/components/Generics/TitleDashboard'
import BupSpinner from '@BUP/components/Loading/Spinner'
import AlertComponent from '@BUP/components/Generics/Alert'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import ResultsSection from '@BUP/components/CourseAssignment/ResultsSection'
import { useGetCurrentPeriodStatus } from '@BUP/hooks/CourseAssignment/useGetCurrentPeriodStatus'
import { useGetAvailableGrades } from '@BUP/hooks/CourseAssignment/useGetAvailableGrades'
import { useGetPeriods } from '@BUP/hooks/CourseAssignment/useGetPeriods'
import { useGradeName } from '@BUP/hooks/CourseAssignment/useGradeName'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { buildCourseGeneratorFileSuffix } from '@BUP/utils/courseGeneratorExport'
import styles from './results.module.scss'

const { Option } = Select

/**
 * Own screen for a period's assignment results (upload + browse the resulting courses), split out
 * from the period dashboard so the roster/exclusions/evaluations workflow and the results review
 * don't compete for space on one long page.
 *
 * A period scopes one grade, so results are per grade too: the grade lives in the `?grade=` query
 * param (the period dashboard links here with its own selection) and can be switched right here,
 * so reviewing every grade's results doesn't need a round trip through the dashboard. Only grades
 * with a period this term are offered (results only exist for those) - a school with 14 grades
 * and periods in 5 would otherwise scroll past 9 empty ones, which is how Kínder went unnoticed.
 */
const CourseAssignmentResultsPage = () => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const navigate = useNavigate()
  const gradeName = useGradeName()
  const organization = useContext(OrganizationContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const { grades: allGrades, isLoading: isLoadingAllGrades } = useGetAvailableGrades()
  const { periods, isLoading: isLoadingPeriods } = useGetPeriods()
  const gradesWithPeriod = new Set(periods.map(period => period.grade))
  // Keep the school order from the grades endpoint (Pre-Kínder, Kínder, 1°...).
  const grades = allGrades.filter(option => gradesWithPeriod.has(option))
  const isLoadingGrades = isLoadingAllGrades || isLoadingPeriods
  const grade = searchParams.get('grade') || grades[0] || null

  const { isLoading, error, period } = useGetCurrentPeriodStatus(grade)

  const renderContent = () => {
    if (isLoading || isLoadingGrades) return <BupSpinner size='large' />

    if (error) {
      return (
        <AlertComponent
          type='error'
          messageKey='common.resources.course-assignment.actions.read.failure'
          error={error}
        />
      )
    }

    if (!period) {
      return <AlertComponent type='info' message={t('noPeriod')} />
    }

    const fileSuffix = buildCourseGeneratorFileSuffix({
      organizationName: organization?.name,
      grade: gradeName(period.grade),
      opensAt: period.opensAt
    })
    // Keyed by periodId so switching grade (only the `?grade=` query param changes, no remount of
    // this page) resets ResultsSection's internal state (selectedVersionId, working copy) instead
    // of carrying over a version id that belongs to the previous grade's period.
    return <ResultsSection key={period.id} periodId={period.id} fileSuffix={fileSuffix} />
  }

  return (
    <PageLayout>
      <>
        <div style={{ marginBottom: '1rem' }}>
          <ButtonComponent
            buttonText={t('backToPeriodButton')}
            colorType='tertiary'
            size='small'
            onClick={() => navigate('/course-assignment/period')}
          />
        </div>
        <TitleDashboard icon={UserIcon} text={t('resultsSectionTitle')} />
        <div className={styles.grade_select_wrapper}>
          <label className={styles.grade_select_label} htmlFor='course-assignment-results-grade-select'>
            {t('gradeSelectLabel')}
          </label>
          <Select
            id='course-assignment-results-grade-select'
            className={styles.grade_select}
            placeholder={t('gradeSelectPlaceholder')}
            value={grade || undefined}
            onChange={value => setSearchParams({ grade: value })}
            loading={isLoadingGrades}
            showSearch
            optionFilterProp='children'
          >
            {grades.map(option => <Option key={option} value={option}>{gradeName(option)}</Option>)}
          </Select>
        </div>
        {renderContent()}
      </>
    </PageLayout>
  )
}

export default CourseAssignmentResultsPage
